import { CollectionIcon } from "@/components/collection/CollectionIcon";
import { MdsChipInput } from "@/design-system/components/input";
import { MdsChip } from "@/design-system/components/input/types";
import { sortCollectionsForChips } from "@/domains/collections/sortCollectionsForChips";
import { MDSButton, MDSButtonVariant } from "@/mds";
import { AppStore, useAppStore } from "@/store";
import { CollectionObservable } from "@/store/collections/CollectionObservable";
import { EmailIntegrationOrganizingManualCollections } from "@/store/email-senders/types";
import { TextMessageIntegrationOrganizingManualCollections } from "@/store/text-message-senders/types";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

interface IntegrationsFormattingCollectionsProps {
  organizingManualCollections: string[] | null;
  setOrganizingManualCollections: (
    value:
      | EmailIntegrationOrganizingManualCollections
      | TextMessageIntegrationOrganizingManualCollections
  ) => void;
}

export const IntegrationsFormattingCollections = observer<IntegrationsFormattingCollectionsProps>(
  ({ organizingManualCollections, setOrganizingManualCollections }) => {
    const { store } = useAppStore();
    const [chips, setChips] = useState<MdsChip[]>([]);

    useEffect(() => {
      const loadChips = async () => {
        const chips = await mapCollectionsToChips(organizingManualCollections, store);
        setChips(chips);
      };

      loadChips();
    }, [organizingManualCollections, store]);

    // local state equals one on the store
    const isDisabled =
      organizingManualCollections?.length === chips.length &&
      organizingManualCollections.every(collection => chips.some(chip => chip.id === collection));

    const getChips = async (query: string) => {
      const collections = await store.collections.getMatchingCollections(query);

      return sortCollectionsForChips(collections).map(collection =>
        mapCollectionToChip(collection)
      );
    };

    const updateChips = (data: MdsChip[] | ((prevChips: MdsChip[]) => MdsChip[])) => {
      const newChips = typeof data === "function" ? data(chips) : data;

      setChips(newChips);
    };

    return (
      <div>
        <MdsChipInput
          chips={chips}
          setChips={updateChips}
          getChips={getChips}
          onSubmit={() => {}}
          placeholder="Type collection name"
          chipIconSize={16}
        />

        <LeftStyles>
          <MDSButton
            onClick={() => {
              // revert to local value
              const loadChips = async () => {
                const chips = await mapCollectionsToChips(organizingManualCollections, store);
                setChips(chips);
              };

              loadChips();
            }}
          >
            Cancel
          </MDSButton>
          <MDSButton
            variant={MDSButtonVariant.Contrast}
            onClick={() => setOrganizingManualCollections(chips.map(chip => chip.id))}
            isDisabled={isDisabled}
          >
            Save Settings
          </MDSButton>
        </LeftStyles>
      </div>
    );
  }
);

const mapCollectionsToChips = async (
  collectionIds: EmailIntegrationOrganizingManualCollections,
  store: AppStore
): Promise<MdsChip[]> => {
  if (!collectionIds) return [];

  const collections = await store.collections.getManyAsync(collectionIds);

  return collections.map(collection => mapCollectionToChip(collection));
};

const mapCollectionToChip = (collection: CollectionObservable): MdsChip => ({
  id: collection.id,
  orderByString: collection.lastInteractedAtByMe ?? collection.lastInteractedAt,
  label: collection.label,
  secondaryLabel: collection.description,
  icon: (size?: number) => (
    <CollectionIcon collectionId={collection.id} size={size} legacy={false} />
  ),
  searchLabels: [collection.label],
});

const LeftStyles = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-top: ${({ theme }) => theme.spacing.sm};
`;
